import { CardContainer } from "@msys/ui";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import {
  IconButton,
  Stack,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import { useUserData } from "../../../auth/useUserData.js";
import { copyTextToClipboard } from "../../../features/addresses/helpers.js";

function CopyableIdRow({ label, value }: { label: string; value: string }) {
  const { t } = useTranslate("Global");
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <MuiTextField
        label={label}
        value={value}
        fullWidth
        size="small"
        slotProps={{
          input: {
            readOnly: true,
            sx: { fontFamily: "monospace", fontSize: 13 },
          },
        }}
      />
      <Tooltip title={t("Copy to clipboard", { ns: "Global" })}>
        <IconButton
          size="small"
          color="secondary"
          onClick={async () => {
            await copyTextToClipboard(value);
            enqueueSnackbar(t("Copied to clipboard", { ns: "Global" }));
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

/**
 * Org-level identifiers external integration admins ask for during setup —
 * preserved from the former "Data & Integrations" box.
 */
export const ConnectionIdsCard = () => {
  const { t } = useTranslate(["Integrations", "OrganisationSettings"]);
  const viewer = useUserData().currentUser;

  if (!viewer) return null;

  return (
    <CardContainer title={t("Your connection IDs")}>
      <Stack spacing={1.5} sx={{ px: 1, pt: 0.5, pb: 1 }}>
        <CopyableIdRow
          label={t("MeisterSystems organisation ID", {
            ns: "OrganisationSettings",
          })}
          value={viewer.organisation.id}
        />
        <CopyableIdRow
          label={t("MeisterSystems user ID", { ns: "OrganisationSettings" })}
          value={viewer.id}
        />
        <Typography variant="caption" color="text.secondary">
          {t("You may be asked for these when setting up an integration.")}
        </Typography>
      </Stack>
    </CardContainer>
  );
};
